import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  Subscription,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeWhile,
  tap,
  timer,
} from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { BULK_OP_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import { RolesService } from '../../../shared/services/roles.service';
import {
  PermissionDTO,
  SeederJobDTO,
} from '../../../shared/types/permission.types';
import { RoleDTO } from '../../../shared/types/role.types';
import { PermissionsTabsComponent } from '../permissions-tabs/permissions-tabs.component';

/** Libellés FR de l'étape courante du seeder (cf. `SeederProgressDTO.currentStep`). */
const SEED_STEP_LABEL: Record<string, string> = {
  pending: 'En attente',
  scopes: 'Scopes',
  resources: 'Resources',
  roles: 'Rôles',
  permissions: 'Permissions',
  policies: 'Policies',
  done: 'Terminé',
};

/** Intervalle de polling du job seeder (ms). */
const SEED_POLL_MS = 2500;

/** Row view-model with precomputed scope preview (avoids re-slicing per CD). */
interface PermissionRow extends PermissionDTO {
  _scopesPreview: string[];
  _scopesOverflow: number;
}

@Component({
  selector: 'app-permissions-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ModalComponent,
    PermissionsTabsComponent,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
  ],
  templateUrl: './permissions-list.component.html',
})
export class PermissionsListComponent implements OnInit {
  private readonly permissionsService = inject(PermissionsService);
  private readonly rolesService = inject(RolesService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** Sorted + scope preview precomputed at fetch. */
  readonly permissions = signal<PermissionRow[]>([]);

  readonly bulkOpOptions = BULK_OP_OPTIONS;
  /** Role names projected as pretty-select options (derived from availableRoles). */
  readonly roleOptions = computed<PrettySelectOption<string>[]>(() =>
    this.availableRoles().map((r) => ({
      value: r.name,
      label: r.name,
      hint: r.description ?? undefined,
    })),
  );

  readonly searchCtrl = this.fb.control('');
  private readonly searchSignal = signal('');

  // Seeder modal — back asynchrone : POST → 202 + jobId, GET /jobs/{id} polled.
  readonly seedOpen = signal(false);
  /** True dès le `POST /seed` et jusqu'à status terminal (SUCCEEDED/FAILED). */
  readonly seedLoading = signal(false);
  readonly seedError = signal<string | null>(null);
  readonly seedJob = signal<SeederJobDTO | null>(null);

  /** True dès qu'on a un job (PENDING / RUNNING). */
  readonly seedRunning = computed(() => {
    const s = this.seedJob()?.status;
    return s === 'PENDING' || s === 'RUNNING';
  });
  readonly seedTerminal = computed(() => {
    const s = this.seedJob()?.status;
    return s === 'SUCCEEDED' || s === 'FAILED';
  });
  /** Libellé FR de l'étape courante. */
  readonly seedCurrentStepLabel = computed(() => {
    const step = this.seedJob()?.progress?.currentStep ?? 'pending';
    return SEED_STEP_LABEL[step] ?? step;
  });
  /** Pourcentage global (somme des compteurs `done` / `total`). */
  readonly seedProgressPercent = computed(() => {
    const p = this.seedJob()?.progress;
    if (!p) return 0;
    const total = p.scopesTotal + p.resourcesTotal + p.rolesTotal +
                  p.permissionsTotal + p.policiesTotal;
    if (total === 0) return 0;
    const done = p.scopesDone + p.resourcesDone + p.rolesDone +
                 p.permissionsDone + p.policiesDone;
    return Math.min(100, Math.round((done / total) * 100));
  });
  /** Compteurs par étape — pour les progress bars individuelles. */
  readonly seedSteps = computed(() => {
    const p = this.seedJob()?.progress;
    if (!p) return [];
    return [
      { key: 'scopes', label: 'Scopes', done: p.scopesDone, total: p.scopesTotal },
      { key: 'resources', label: 'Resources', done: p.resourcesDone, total: p.resourcesTotal },
      { key: 'roles', label: 'Rôles', done: p.rolesDone, total: p.rolesTotal },
      { key: 'permissions', label: 'Permissions', done: p.permissionsDone, total: p.permissionsTotal },
      { key: 'policies', label: 'Policies', done: p.policiesDone, total: p.policiesTotal },
    ];
  });

  /** Souscription du polling — annulée si on relance ou si le composant est détruit. */
  private pollSub?: Subscription;

  // Bulk role assignment modal
  readonly bulkOpen = signal(false);
  readonly bulkLoading = signal(false);
  readonly bulkError = signal<string | null>(null);
  readonly bulkResult = signal<string | null>(null);
  readonly availableRoles = signal<RoleDTO[]>([]);
  readonly rolesLoading = signal(false);
  readonly selectedPermissionIds = signal<Set<string>>(new Set());
  private readonly bulkFilterSignal = signal('');
  readonly bulkForm = this.fb.group({
    roleName: ['', [Validators.required]],
    operation: ['ADD' as 'ADD' | 'REMOVE'],
    filter: [''],
  });

  readonly filtered = computed(() => {
    const q = this.searchSignal().toLowerCase().trim();
    if (!q) return this.permissions();
    return this.permissions().filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.displayName?.toLowerCase().includes(q) ?? false) ||
        (p.type?.toLowerCase().includes(q) ?? false),
    );
  });

  readonly filteredBulkPerms = computed(() => {
    const q = this.bulkFilterSignal().toLowerCase().trim();
    if (!q) return this.permissions();
    return this.permissions().filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.displayName?.toLowerCase().includes(q) ?? false),
    );
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((v) => this.searchSignal.set(v));

    this.bulkForm.controls.filter.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((v) => this.bulkFilterSignal.set(v ?? ''));

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.permissionsService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Sort once after fetch and precompute scope preview.
          const rows: PermissionRow[] = [...(res ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => {
              const total = p.scopes?.length ?? 0;
              return {
                ...p,
                _scopesPreview: (p.scopes ?? []).slice(0, 3),
                _scopesOverflow: Math.max(0, total - 3),
              };
            });
          this.permissions.set(rows);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.permissions.set([]);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  // ── Bulk role assignment ───────────────────────────────────────────────
  openBulk(): void {
    this.bulkError.set(null);
    this.bulkResult.set(null);
    this.selectedPermissionIds.set(new Set());
    this.bulkForm.reset({ roleName: '', operation: 'ADD', filter: '' });
    this.bulkFilterSignal.set('');
    this.bulkOpen.set(true);
    if (this.availableRoles().length === 0) this.loadAvailableRoles();
  }
  closeBulk(): void {
    if (this.bulkLoading()) return;
    this.bulkOpen.set(false);
  }
  private loadAvailableRoles(): void {
    this.rolesLoading.set(true);
    this.rolesService.list().subscribe({
      next: (roles) => {
        this.availableRoles.set(
          (roles ?? []).sort((a, b) => a.name.localeCompare(b.name)),
        );
        this.rolesLoading.set(false);
      },
      error: () => this.rolesLoading.set(false),
    });
  }
  togglePerm(id: string | undefined): void {
    if (!id) return;
    const next = new Set(this.selectedPermissionIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selectedPermissionIds.set(next);
  }
  isPermSelected(id: string | undefined): boolean {
    return !!id && this.selectedPermissionIds().has(id);
  }
  submitBulk(): void {
    if (this.bulkForm.invalid || this.bulkLoading()) {
      this.bulkForm.markAllAsTouched();
      return;
    }
    const ids = [...this.selectedPermissionIds()];
    if (ids.length === 0) {
      this.bulkError.set('Sélectionne au moins une permission.');
      return;
    }
    const raw = this.bulkForm.getRawValue();
    this.bulkLoading.set(true);
    this.bulkError.set(null);
    this.permissionsService
      .bulkRoleAssignment({
        roleName: raw.roleName,
        permissionIds: ids,
        operation: raw.operation,
      })
      .subscribe({
        next: () => {
          this.bulkLoading.set(false);
          const verb = raw.operation === 'ADD' ? 'assigné' : 'retiré';
          this.bulkResult.set(
            `Rôle ${raw.roleName} ${verb} sur ${ids.length} permission(s).`,
          );
          this.load();
        },
        error: (err: unknown) => {
          this.bulkLoading.set(false);
          this.bulkError.set(toErrorMessage(err, "L'opération bulk a échoué."));
        },
      });
  }

  // ── Seeder (async) ─────────────────────────────────────────────────────
  openSeed(): void {
    this.seedError.set(null);
    this.seedJob.set(null);
    this.seedOpen.set(true);
  }
  /** Fermeture autorisée seulement quand le job est terminé (ou pas démarré). */
  closeSeed(): void {
    if (this.seedRunning()) return;
    this.seedOpen.set(false);
  }
  runSeed(): void {
    if (this.seedRunning()) return;
    this.seedLoading.set(true);
    this.seedError.set(null);
    this.seedJob.set(null);
    this.permissionsService.startSeed().subscribe({
      next: (job) => this.startSeedPolling(job),
      error: (err: unknown) => {
        // 409 = un job tourne déjà → le body contient ce job ; on resume le polling.
        const running = this.extractConflictJob(err);
        if (running) {
          this.startSeedPolling(running);
          return;
        }
        this.seedLoading.set(false);
        this.seedError.set(toErrorMessage(err, 'Le seeder a échoué.'));
      },
    });
  }

  /** Extrait le `SeederJobDTO` retourné dans le body d'une 409 Conflict. */
  private extractConflictJob(err: unknown): SeederJobDTO | null {
    if (err instanceof HttpErrorResponse && err.status === 409) {
      const body = err.error as Partial<SeederJobDTO> | null;
      if (body && typeof body === 'object' && typeof body.jobId === 'string') {
        return body as SeederJobDTO;
      }
    }
    return null;
  }

  /** Lance le polling jusqu'à status terminal — refresh la liste si SUCCEEDED. */
  private startSeedPolling(initial: SeederJobDTO): void {
    this.seedJob.set(initial);
    this.pollSub?.unsubscribe();
    this.pollSub = timer(SEED_POLL_MS, SEED_POLL_MS)
      .pipe(
        switchMap(() => this.permissionsService.getSeedJob(initial.jobId)),
        tap((job) => this.seedJob.set(job)),
        // inclusive=true → on garde la valeur terminale qui déclenche complete()
        takeWhile(
          (job) => job.status === 'PENDING' || job.status === 'RUNNING',
          true,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (err: unknown) => {
          this.seedLoading.set(false);
          this.seedError.set(
            toErrorMessage(err, 'Suivi du job interrompu.'),
          );
        },
        complete: () => {
          this.seedLoading.set(false);
          if (this.seedJob()?.status === 'SUCCEEDED') this.load();
        },
      });
  }
}
